import React, { useState, useEffect } from 'react';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import HelpIcon from './Documentation/HelpIcon';
import { IoCopyOutline, IoCheckmark } from 'react-icons/io5';
import { updateInstitutionSettings, getInstitutionAdmins, inviteInstitutionAdmin, resendAdminInvitation, revokeAdminInvitation, removeInstitutionAdmin } from '../services/apiService'; // Import the new API functions
import AssetManager from '../services/AssetManager';

// Assuming the user object containing the token is passed as a prop from App.jsx
function SchoolSettingsPage({ user }) {
    const [schoolSettings, setSchoolSettings] = useState({
        name: '',
        urlEndpoint: '',
        icon: '',
        website: '',
        minHour: 8,
        maxHour: 18,
        activeMonday: true,
        activeTuesday: true,
        activeWednesday: true,
        activeThursday: true,
        activeFriday: true,
        activeSaturday: false,
        activeSunday: false
    });
    const [isLoading, setIsLoading] = useState(true); // Start in loading state
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // --- Fetch settings on component mount and when user/token changes ---
    useEffect(() => {
        // This effect runs when the component mounts and whenever the 'user' prop changes.

        if (!user?.token) {
            // If user or user.token is not available, we cannot fetch settings.
            // Set loading to false and show an authentication required message.
            console.warn("User or token not available for settings page.");
            setError("Authentication required to view settings.");
            setIsLoading(false); // Stop loading spinner
            // Do NOT proceed to attempt fetching
            return;
        }

        // If we reach here, user and user.token are available.
        // Define the async fetch function inside the effect where it's needed.
        const fetchSettings = async () => {
            console.log("Attempting to fetch school settings with token..."); // Added log for debugging
            setIsLoading(true); // Start loading spinner
            setError(null); // Clear any previous errors

            try {
                const assetManager = AssetManager.getInstance();
                const settings = await assetManager.fetchInstitutionSettings();
                console.log("API Response received for settings:", settings); // Log the response
                // AssetManager already maps snake_case to camelCase, so we can use settings directly
                const mappedSettings = {
                    ...settings
                };
                setSchoolSettings(mappedSettings);

                // Also load admins/invitations
                try {
                    const { admins: adminList, invitations: inviteList } = await getInstitutionAdmins();
                    setAdmins(adminList || []);
                    setInvitations(inviteList || []);
                } catch (innerErr) {
                    console.warn('Failed to fetch admins/invitations:', innerErr);
                }
                console.log("Fetched school settings and updated state:", mappedSettings);

            } catch (err) {
                console.error("Failed to fetch school settings:", err);
                setError(`Failed to load school settings: ${err.message}`);
            } finally {
                setIsLoading(false); // Stop loading spinner regardless of success or error
            }
        };

        // Call the fetch function now that we've confirmed user and token are available.
        fetchSettings();

        // Optional: Cleanup function if needed (e.g., cancelling ongoing fetches)
        // return () => { /* cleanup code */ };

    }, [user]); // Dependency array: re-run this effect if the 'user' object changes.

    // --- Handle input changes ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Basic validation for urlEndpoint format (lowercase, numbers, hyphens)
        if (name === 'urlEndpoint') {
            if (value !== '' && !/^[a-z0-9-]+$/.test(value)) {
                // Optionally show a temporary validation message to the user
                console.warn("Invalid characters for URL endpoint. Use only lowercase letters, numbers, and hyphens.");
                // You might choose to update state but mark it as invalid, or just prevent update.
                // For now, we'll allow the update but rely on form submission validation.
            }
        }

        // Convert time range values to numbers
        if (name === 'minHour' || name === 'maxHour') {
            processedValue = parseInt(value, 10);
            if (isNaN(processedValue)) processedValue = value; // Keep original if not a valid number
        }

        setSchoolSettings(prev => ({
            ...prev,
            [name]: processedValue
        }));
        // Clear save success message on any change
        setSaveSuccess(false);
    };

    // --- Handle day toggle changes ---
    const handleDayToggle = (dayName) => {
        setSchoolSettings(prev => ({
            ...prev,
            [dayName]: !prev[dayName]
        }));
        // Clear save success message on any change
        setSaveSuccess(false);
    };

    // --- Handle form submission (Save Settings) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user || !user.token) {
            setError("Authentication token not available. Cannot save.");
            return;
        }
        // Use the state value, which is in camelCase (urlEndpoint)
        if (!schoolSettings.name.trim() || !schoolSettings.urlEndpoint.trim()) {
            setError("School Name and URL Endpoint are required.");
            setSaveSuccess(false);
            return;
        }
        if (!/^[a-z0-9-]+$/.test(schoolSettings.urlEndpoint)) {
            setError("URL Endpoint must contain only lowercase letters, numbers, and hyphens.");
            setSaveSuccess(false);
            return;
        }
        if (schoolSettings.website && !/^https?:\/\/.+/.test(schoolSettings.website)) {
            setError("Website URL must start with http:// or https://");
            setSaveSuccess(false);
            return;
        }

        // Validate time range
        const minHour = parseInt(schoolSettings.minHour, 10);
        const maxHour = parseInt(schoolSettings.maxHour, 10);

        if (isNaN(minHour) || minHour < 0 || minHour > 23) {
            setError("Minimum hour must be between 0 and 23.");
            setSaveSuccess(false);
            return;
        }

        if (isNaN(maxHour) || maxHour < 1 || maxHour > 24) {
            setError("Maximum hour must be between 1 and 24.");
            setSaveSuccess(false);
            return;
        }

        if (minHour >= maxHour) {
            setError("Minimum hour must be less than maximum hour.");
            setSaveSuccess(false);
            return;
        }

        // Validate that at least one day is selected
        const hasAtLeastOneDay = schoolSettings.activeMonday || schoolSettings.activeTuesday ||
            schoolSettings.activeWednesday || schoolSettings.activeThursday ||
            schoolSettings.activeFriday || schoolSettings.activeSaturday ||
            schoolSettings.activeSunday;

        if (!hasAtLeastOneDay) {
            setError("At least one day must be selected for the calendar to function.");
            setSaveSuccess(false);
            return;
        }

        setError(null);
        setIsSaving(true);
        setSaveSuccess(false); // Clear previous success message

        try {
            // Prepare data for the API. The API documentation uses `urlEndpoint`
            // in the request body example for PUT. Send data as it is in state.
            const dataToSave = {
                name: schoolSettings.name,
                urlEndpoint: schoolSettings.urlEndpoint, // Send camelCase as per API doc example
                icon: schoolSettings.icon,
                website: schoolSettings.website,
                minHour: minHour,
                maxHour: maxHour,
                activeMonday: schoolSettings.activeMonday,
                activeTuesday: schoolSettings.activeTuesday,
                activeWednesday: schoolSettings.activeWednesday,
                activeThursday: schoolSettings.activeThursday,
                activeFriday: schoolSettings.activeFriday,
                activeSaturday: schoolSettings.activeSaturday,
                activeSunday: schoolSettings.activeSunday
            };
            console.log("Attempting to save settings:", dataToSave);
            console.log("Current schoolSettings state:", schoolSettings);
            console.log("Day values in state:", {
                activeMonday: schoolSettings.activeMonday,
                activeTuesday: schoolSettings.activeTuesday,
                activeWednesday: schoolSettings.activeWednesday,
                activeThursday: schoolSettings.activeThursday,
                activeFriday: schoolSettings.activeFriday,
                activeSaturday: schoolSettings.activeSaturday,
                activeSunday: schoolSettings.activeSunday
            });

            const updatedSettings = await updateInstitutionSettings(dataToSave, user.token);
            console.log("School settings saved:", updatedSettings);

            // The API returns snake_case (url_endpoint, min_hour, max_hour, day fields) after save, map it back to camelCase for state
            const mappedUpdatedSettings = {
                ...updatedSettings,
                urlEndpoint: updatedSettings.url_endpoint,
                minHour: updatedSettings.min_hour || 8,
                maxHour: updatedSettings.max_hour || 18,
                activeMonday: updatedSettings.active_monday !== undefined ? updatedSettings.active_monday : true,
                activeTuesday: updatedSettings.active_tuesday !== undefined ? updatedSettings.active_tuesday : true,
                activeWednesday: updatedSettings.active_wednesday !== undefined ? updatedSettings.active_wednesday : true,
                activeThursday: updatedSettings.active_thursday !== undefined ? updatedSettings.active_thursday : true,
                activeFriday: updatedSettings.active_friday !== undefined ? updatedSettings.active_friday : true,
                activeSaturday: updatedSettings.active_saturday !== undefined ? updatedSettings.active_saturday : false,
                activeSunday: updatedSettings.active_sunday !== undefined ? updatedSettings.active_sunday : false
            };

            setSchoolSettings(mappedUpdatedSettings); // Update state with the saved data from API response
            setSaveSuccess(true); // Indicate success
            // Note: The useEffect will re-fetch on user change, but manual state update gives immediate feedback.

        } catch (err) {
            console.error("Failed to save school settings:", err);
            // Attempt to parse API error response if available
            const apiErrorMessage = err.response?.data?.message || err.message;
            setError(`Failed to save settings: ${apiErrorMessage}`);
            setSaveSuccess(false); // Ensure success message is not shown on error
        } finally {
            setIsSaving(false);
        }
    };

    // --- Copy URL to clipboard ---
    const copyUrlToClipboard = async (type) => {
        try {
            let fullUrl;
            if (type === 'students') {
                fullUrl = `https://students.pi-schedule.com/${schoolSettings.urlEndpoint}`;
            } else if (type === 'teachers') {
                fullUrl = `https://teachers.pi-schedule.com/${schoolSettings.urlEndpoint}`;
            } else {
                // Fallback to students URL for backward compatibility
                fullUrl = `https://students.pi-schedule.com/${schoolSettings.urlEndpoint}`;
            }

            await navigator.clipboard.writeText(fullUrl);
            setCopySuccess(type);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy URL to clipboard:', err);
            setError('Failed to copy URL to clipboard');
        }
    };

    // --- Render Loading/Error states ---
    // Display loading state initially or when fetching/saving
    if (isLoading || isSaving) {
        return <div className="p-6 text-center">
            {isSaving ? 'Saving settings...' : 'Loading school settings...'}
        </div>;
    }

    // If no user or token is available after loading is complete, display an auth error
    // This check is also handled at the top of useEffect, but serves as a final display state.
    if (!user?.token) {
        return <div className="p-6 text-center text-red-600">Authentication required to view settings.</div>;
    }

    return (
        <div className="max-w-2xl mx-auto">
            <Card className="p-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-6">School Settings</h2>
                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                        Error: {error}
                    </div>
                )}
                {saveSuccess && (
                    <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                        Settings saved successfully!
                    </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                            School Name
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={schoolSettings.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="Enter school name"
                            required
                            disabled={isSaving}
                        />
                    </div>

                    <div>
                        <div className="flex items-center mb-1">
                            <label htmlFor="urlEndpoint" className="block text-sm font-medium text-gray-700">
                                Public URL Endpoint
                            </label>
                            <HelpIcon
                                documentationId="settings-url-endpoint"
                                position="inline"
                                className="ml-2"
                                size={14}
                            />
                        </div>
                        <div className="flex items-center mb-3">
                            <span className="text-gray-500 mr-2 text-sm whitespace-nowrap flex-shrink-0">/</span>
                            <input
                                type="text"
                                id="urlEndpoint"
                                name="urlEndpoint" // Use camelCase name for state binding
                                value={schoolSettings.urlEndpoint}
                                onChange={handleChange}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                placeholder="school-name"
                                required
                                disabled={isSaving}
                                pattern="^[a-z0-9-]+$" // Add pattern for client-side validation
                                title="Only lowercase letters, numbers, and hyphens are allowed."
                            />
                        </div>

                        {/* Public URLs Display */}
                        <div className="space-y-2">
                            <div className="text-sm font-medium text-gray-700 mb-2">Public URLs:</div>

                            {/* Students URL */}
                            <div className="flex items-center">
                                <div className="flex items-center flex-1">
                                    <span className="text-sm text-gray-800">
                                        students.pi-schedule.com/{schoolSettings.urlEndpoint || 'school-name'}
                                    </span>
                                    <HelpIcon
                                        documentationId="settings-students-url"
                                        position="inline"
                                        className="ml-2"
                                        size={14}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => copyUrlToClipboard('students')}
                                    disabled={!schoolSettings.urlEndpoint || isSaving}
                                    className={`ml-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${copySuccess === 'students'
                                        ? 'bg-green-100 text-green-700 border border-green-300'
                                        : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500'
                                        }`}
                                    title="Copy students URL to clipboard"
                                >
                                    {copySuccess === 'students' ? <IoCheckmark size={14} /> : <IoCopyOutline size={14} />}
                                </button>
                            </div>

                            {/* Teachers URL */}
                            <div className="flex items-center">
                                <div className="flex items-center flex-1">
                                    <span className="text-sm text-gray-800">
                                        teachers.pi-schedule.com/{schoolSettings.urlEndpoint || 'school-name'}
                                    </span>
                                    <HelpIcon
                                        documentationId="settings-teachers-url"
                                        position="inline"
                                        className="ml-2"
                                        size={14}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => copyUrlToClipboard('teachers')}
                                    disabled={!schoolSettings.urlEndpoint || isSaving}
                                    className={`ml-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${copySuccess === 'teachers'
                                        ? 'bg-green-100 text-green-700 border border-green-300'
                                        : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500'
                                        }`}
                                    title="Copy teachers URL to clipboard"
                                >
                                    {copySuccess === 'teachers' ? <IoCheckmark size={14} /> : <IoCopyOutline size={14} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-1">
                            School Icon URL (Optional)
                        </label>
                        <input
                            type="url"
                            id="icon"
                            name="icon"
                            value={schoolSettings.icon}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="https://example.com/school-icon.png"
                            disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500 mt-1">Link to your school's logo or icon image.</p>
                    </div>

                    <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                            School Website (Optional)
                        </label>
                        <input
                            type="url"
                            id="website"
                            name="website"
                            value={schoolSettings.website}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            placeholder="https://school-website.com"
                            disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500 mt-1">Link to your official school website.</p>
                    </div>

                    {/* Calendar Days Section */}
                    <div className="border-t pt-6">
                        <div className="flex items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-800">Calendar Days</h3>
                            <HelpIcon
                                documentationId="settings-calendar-days"
                                position="inline"
                                className="ml-2"
                                size={16}
                            />
                        </div>
                        <p className="text-sm text-gray-600 mb-4">Select which days of the week should be displayed in the calendar.</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { key: 'activeMonday', label: 'Monday', default: true },
                                { key: 'activeTuesday', label: 'Tuesday', default: true },
                                { key: 'activeWednesday', label: 'Wednesday', default: true },
                                { key: 'activeThursday', label: 'Thursday', default: true },
                                { key: 'activeFriday', label: 'Friday', default: true },
                                { key: 'activeSaturday', label: 'Saturday', default: false },
                                { key: 'activeSunday', label: 'Sunday', default: false }
                            ].map(({ key, label, default: isDefault }) => (
                                <div key={key} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id={key}
                                        checked={schoolSettings[key]}
                                        onChange={() => handleDayToggle(key)}
                                        disabled={isSaving}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                                    />
                                    <label htmlFor={key} className="text-sm font-medium text-gray-700">
                                        {label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">At least one day must be selected for the calendar to function properly.</p>
                    </div>

                    {/* School Hours Section */}
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-800 mb-4">School Hours</h3>
                        <p className="text-sm text-gray-600 mb-4">Define the time range for calendar display. This affects how the schedule calendar is displayed.</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="minHour" className="block text-sm font-medium text-gray-700 mb-1">
                                    Start Hour
                                </label>
                                <select
                                    id="minHour"
                                    name="minHour"
                                    value={schoolSettings.minHour}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                    disabled={isSaving}
                                >
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {String(i).padStart(2, '0')}:00 {i === 0 ? '(Midnight)' : i < 12 ? 'AM' : 'PM'}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">When the school day starts (e.g., 8 for 8:00 AM)</p>
                            </div>

                            <div>
                                <label htmlFor="maxHour" className="block text-sm font-medium text-gray-700 mb-1">
                                    End Hour
                                </label>
                                <select
                                    id="maxHour"
                                    name="maxHour"
                                    value={schoolSettings.maxHour}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                    disabled={isSaving}
                                >
                                    {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                                        <option key={hour} value={hour}>
                                            {String(hour).padStart(2, '0')}:00 {hour === 24 ? '(Midnight)' : hour <= 12 ? (hour === 12 ? 'PM' : 'AM') : 'PM'}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">When the school day ends (e.g., 18 for 6:00 PM)</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        {/* Disable button if saving or required fields are empty */}
                        <Button type="submit" variant="primary" disabled={isSaving || !schoolSettings.name.trim() || !schoolSettings.urlEndpoint.trim()}>
                            {isSaving ? 'Saving...' : 'Save Settings'}
                        </Button>
                    </div>
                </form>
                {/* Administrators Section */}
                <div className="border-t pt-6 mt-8">
                    <div className="flex items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-800">Administrators</h3>
                        <HelpIcon
                            documentationId="settings-admins"
                            position="inline"
                            className="ml-2"
                            size={16}
                        />
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Manage institution admins and invitations.</p>

                    {/* Current Admins */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Admins</h4>
                        <ul className="space-y-2">
                            {admins.length === 0 && (
                                <li className="text-sm text-gray-500">No admins found.</li>
                            )}
                            {admins.map((a) => (
                                <li key={a.id} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-800">{a.email}</span>
                                    <Button
                                        variant="secondary"
                                        className="text-xs py-1 px-2"
                                        disabled={admins.length <= 1 || a.email === user?.email}
                                        onClick={async () => {
                                            try {
                                                await removeInstitutionAdmin(a.id);
                                                setAdmins((prev) => prev.filter((x) => x.id !== a.id));
                                            } catch (err) {
                                                setError(err.message || 'Failed to remove admin');
                                            }
                                        }}
                                    >Remove</Button>
                                </li>
                            ))}
                        </ul>
                        <p className="text-xs text-gray-500 mt-2">You cannot remove the last admin.</p>
                    </div>

                    {/* Pending Invitations */}
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Pending Invitations</h4>
                        <ul className="space-y-2">
                            {invitations.filter(i => i.status === 'pending').length === 0 && (
                                <li className="text-sm text-gray-500">No pending invitations.</li>
                            )}
                            {invitations.filter(i => i.status === 'pending').map((inv) => (
                                <li key={inv.id} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-800">{inv.email}</span>
                                    <div className="space-x-2">
                                        <Button
                                            variant="secondary"
                                            className="text-xs py-1 px-2"
                                            onClick={async () => {
                                                try {
                                                    await resendAdminInvitation(inv.id);
                                                } catch (err) {
                                                    setError(err.message || 'Failed to resend');
                                                }
                                            }}
                                        >Resend</Button>
                                        <Button
                                            variant="secondary"
                                            className="text-xs py-1 px-2"
                                            onClick={async () => {
                                                try {
                                                    await revokeAdminInvitation(inv.id);
                                                    setInvitations((prev) => prev.map(x => x.id === inv.id ? { ...x, status: 'revoked' } : x));
                                                } catch (err) {
                                                    setError(err.message || 'Failed to revoke');
                                                }
                                            }}
                                        >Revoke</Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Invite Admin */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Invite New Admin</h4>
                        <div className="flex items-center space-x-2">
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="admin@example.com"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                            />
                            <Button
                                variant="primary"
                                disabled={!inviteEmail || isSaving || inviteSubmitting}
                                onClick={async () => {
                                    try {
                                        setError(null);
                                        setInviteSuccess('');
                                        setInviteSubmitting(true);
                                        const created = await inviteInstitutionAdmin(inviteEmail);
                                        setInvitations(prev => [...prev, created]);
                                        setInviteEmail('');
                                        setInviteSuccess(`Invitation sent to ${created.email}.`);
                                    } catch (err) {
                                        setError(err.message || 'Failed to send invitation');
                                    } finally {
                                        setInviteSubmitting(false);
                                    }
                                }}
                            >{inviteSubmitting ? 'Sending…' : 'Send Invite'}</Button>
                        </div>
                        {inviteSuccess && (
                            <div className="mt-2 p-2 bg-green-100 border border-green-400 text-green-700 rounded text-xs">
                                {inviteSuccess}
                            </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">Invitees receive an email with a link to accept. Link expires in 7 days.</p>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default SchoolSettingsPage; 
